import * as validators from "@/utils/validators";

import { COUNTRIES, FUNNEL_OPTIONS } from "@/constants/countries";
import {
  EXPERIMENT_EVENTS,
  ONBOARDING_EVENTS,
} from "@/constants/analyticsEvents";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  convexQuery,
  useConvexAction,
  useConvexMutation,
} from "@convex-dev/react-query";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";

import { Route as ApplyPartnershipRoute } from "@/routes/_app/_auth/dashboard/_layout.partnerships.apply";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Route as DashboardRoute } from "@/routes/_app/_auth/dashboard/_layout.index";
import { Input } from "@/components/ui/input";
import { Loader2 } from "lucide-react";
import { SearchableCountrySelect } from "@/components/ui/searchable-country-select";
import { api } from "~/convex/_generated/api";
import { getExperimentGroup } from "@/utils/experiments";
import { useAccount } from "@/state/useAccount";
import { useForm } from "@tanstack/react-form";
import { useMixpanel } from "@/hooks/useMixpanel";
import { z } from "zod";
import { zodValidator } from "@tanstack/zod-form-adapter";

export const Route = createFileRoute("/_app/_auth/onboarding/_layout/name")({
  component: OnboardingName,
  beforeLoad: () => ({
    title: `Getting Started`,
  }),
});

type OnboardingStep =
  | "welcome"
  | "name"
  | "country"
  | "funnel"
  | "weeklyPlatforms"
  | "channelLink"
  | "brandDeal";

export default function OnboardingName() {
  const { data: user } = useQuery(convexQuery(api.app.getCurrentUser, {}));
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [currentStep, setCurrentStep] = useState<OnboardingStep>("welcome");
  const [selectedFunnel, setSelectedFunnel] = useState("");
  const [selectedWeeklyPlatforms, setSelectedWeeklyPlatforms] = useState<
    string[]
  >([]);
  const { appId } = useAccount();

  // Store all onboarding answers in state
  const [onboardingData, setOnboardingData] = useState({
    name: "",
    placeOfResidence: "",
    contentPlatform: {
      daily: [] as Array<"youtube" | "tiktok" | "instagram" | "youtube-shorts">,
      weekly: [] as Array<
        "youtube" | "tiktok" | "instagram" | "youtube-shorts"
      >,
    },
    mainVideoChannelLink: "",
    hasBeenPaidForBrandDeal: undefined as boolean | undefined,
    howDidYouHearAboutUs: "",
  });

  const { mutateAsync: updateUserDetails } = useMutation({
    mutationFn: useConvexMutation(api.app.updateUserDetails),
  });
  const navigate = useNavigate();
  const { track } = useMixpanel();

  // Determine which step to show based on user data
  useEffect(() => {
    if (!user) return;

    // Always start with welcome screen, unless user has already started onboarding
    if (!user.name && !user.placeOfResidence) {
      setCurrentStep("welcome");
    } else if (!user.name) {
      setCurrentStep("name");
    } else if (!user.placeOfResidence) {
      setCurrentStep("country");
    } else if (!user.contentPlatform?.weekly) {
      setCurrentStep("weeklyPlatforms");
    } else if (!user.mainVideoChannelLink) {
      setCurrentStep("channelLink");
    } else if (user.hasBeenPaidForBrandDeal === undefined) {
      setCurrentStep("brandDeal");
    } else if (!user.howDidYouHearAboutUs) {
      setCurrentStep("funnel");
    } else {
      // All steps completed, navigate to next page
      const experimentGroup = getExperimentGroup(user._id);
      track(EXPERIMENT_EVENTS.ONBOARDING_REDIRECT, {
        user_id: user?._id,
        variant: "directory",
        experiment_group: experimentGroup,
        timestamp: new Date().toISOString(),
      });
      if (appId) {
        navigate({ to: DashboardRoute.fullPath, replace: true });
      } else {
        navigate({ to: ApplyPartnershipRoute.fullPath });
      }
    }
  }, [user, navigate]);

  const nameForm = useForm({
    validatorAdapter: zodValidator(),
    defaultValues: {
      name: "",
    },
    onSubmit: async ({ value }) => {
      setIsSubmitting(true);
      try {
        // Store in state, don't update database yet
        setOnboardingData((prev) => ({ ...prev, name: value.name }));

        // Track in Mixpanel
        track(ONBOARDING_EVENTS.ONBOARDING_STEP_COMPLETED, {
          user_id: user?._id,
          step: "name",
          name: value.name,
          timestamp: new Date().toISOString(),
        });

        setCurrentStep("country");
      } finally {
        setIsSubmitting(false);
      }
    },
  });

  const countryForm = useForm({
    validatorAdapter: zodValidator(),
    defaultValues: {
      country: "",
    },
    onSubmit: async ({ value }) => {
      setIsSubmitting(true);
      try {
        // Store in state, don't update database yet
        setOnboardingData((prev) => ({
          ...prev,
          placeOfResidence: value.country,
        }));

        // Track in Mixpanel
        track(ONBOARDING_EVENTS.ONBOARDING_STEP_COMPLETED, {
          user_id: user?._id,
          step: "country",
          country: value.country,
          timestamp: new Date().toISOString(),
        });

        setCurrentStep("weeklyPlatforms");
      } finally {
        setIsSubmitting(false);
      }
    },
  });

  const funnelForm = useForm({
    validatorAdapter: zodValidator(),
    defaultValues: {
      funnel: "",
      customFunnel: "",
    },
    onSubmit: async ({ value }) => {
      setIsSubmitting(true);
      try {
        if (!user?._id) throw new Error("User not found");

        const funnelValue =
          selectedFunnel === "other" ? value.customFunnel : value.funnel;

        // Store in state
        setOnboardingData((prev) => ({
          ...prev,
          howDidYouHearAboutUs: funnelValue,
        }));

        // Track final step in Mixpanel
        track(ONBOARDING_EVENTS.ONBOARDING_STEP_COMPLETED, {
          user_id: user?._id,
          step: "funnel",
          funnel_source: funnelValue,
          is_custom: selectedFunnel === "other",
          timestamp: new Date().toISOString(),
        });

        // Now update the database with ALL collected data at once
        await updateUserDetails({
          name: onboardingData.name || undefined,
          placeOfResidence: onboardingData.placeOfResidence || undefined,
          howDidYouHearAboutUs: funnelValue || undefined,
          contentPlatform: {
            daily: undefined,
            weekly:
              onboardingData.contentPlatform.weekly.length > 0
                ? onboardingData.contentPlatform.weekly
                : undefined,
          },
          mainVideoChannelLink:
            onboardingData.mainVideoChannelLink || undefined,
          hasBeenPaidForBrandDeal: onboardingData.hasBeenPaidForBrandDeal,
        });

        // Track completion in Mixpanel
        track(ONBOARDING_EVENTS.ONBOARDING_PROFILE_DETAILS_COMPLETED, {
          user_id: user?._id,
          name: onboardingData.name,
          country: onboardingData.placeOfResidence,
          funnel_source: funnelValue,
          weekly_platforms: onboardingData.contentPlatform.weekly,
          weekly_platform_count: onboardingData.contentPlatform.weekly.length,
          has_channel: !!onboardingData.mainVideoChannelLink,
          hasBeenPaidForBrandDeal: onboardingData.hasBeenPaidForBrandDeal,
          timestamp: new Date().toISOString(),
        });

        // All steps completed, navigate to next page
        const experimentGroup = getExperimentGroup(user._id);
        track(EXPERIMENT_EVENTS.ONBOARDING_REDIRECT, {
          user_id: user._id,
          variant: "directory",
          experiment_group: experimentGroup,
          timestamp: new Date().toISOString(),
        });
        if (appId) {
          navigate({ to: DashboardRoute.fullPath, replace: true });
        } else {
          navigate({ to: ApplyPartnershipRoute.fullPath });
        }
      } finally {
        setIsSubmitting(false);
      }
    },
  });

  const weeklyPlatformsForm = useForm({
    validatorAdapter: zodValidator(),
    defaultValues: {
      platforms: [] as string[],
    },
    onSubmit: async ({ value }) => {
      setIsSubmitting(true);
      try {
        // Store in state, don't update database yet
        setOnboardingData((prev) => ({
          ...prev,
          contentPlatform: {
            daily: prev.contentPlatform.daily,
            weekly: value.platforms as Array<
              "youtube" | "tiktok" | "instagram" | "youtube-shorts"
            >,
          },
        }));

        // Track in Mixpanel
        track(ONBOARDING_EVENTS.ONBOARDING_STEP_COMPLETED, {
          user_id: user?._id,
          step: "weeklyPlatforms",
          platforms: value.platforms,
          platform_count: value.platforms.length,
          skipped: value.platforms.length === 0,
          timestamp: new Date().toISOString(),
        });

        setCurrentStep("channelLink");
      } finally {
        setIsSubmitting(false);
      }
    },
  });

  const trackChannelVideos = useConvexAction(
    api.socials.trackChannelVideosToMixpanel,
  );

  const channelLinkForm = useForm({
    validatorAdapter: zodValidator(),
    defaultValues: {
      channelLink: "",
    },
    onSubmit: async ({ value }) => {
      setIsSubmitting(true);
      try {
        // Store in state
        setOnboardingData((prev) => ({
          ...prev,
          mainVideoChannelLink: value.channelLink || "",
        }));

        // Track in Mixpanel
        track(ONBOARDING_EVENTS.ONBOARDING_STEP_COMPLETED, {
          user_id: user?._id,
          step: "channelLink",
          has_channel: !!value.channelLink,
          channel_link: value.channelLink,
          timestamp: new Date().toISOString(),
        });

        // If user provided a channel link, fetch videos and send to Mixpanel
        if (value.channelLink && value.channelLink.trim()) {
          try {
            await trackChannelVideos({
              channelLink: value.channelLink,
            });
            console.log("Successfully tracked channel videos to Mixpanel");
          } catch (error) {
            console.error("Error tracking channel videos:", error);
            // Don't block the onboarding flow if this fails
          }
        }

        setCurrentStep("brandDeal");
      } finally {
        setIsSubmitting(false);
      }
    },
  });

  const brandDealForm = useForm({
    validatorAdapter: zodValidator(),
    defaultValues: {
      hasBeenPaidForBrandDeal: undefined as boolean | undefined,
    },
    onSubmit: async ({ value }) => {
      setIsSubmitting(true);
      try {
        // Store in state, don't update database yet
        setOnboardingData((prev) => ({
          ...prev,
          hasBeenPaidForBrandDeal: value.hasBeenPaidForBrandDeal,
        }));

        // Track in Mixpanel
        track(ONBOARDING_EVENTS.ONBOARDING_STEP_COMPLETED, {
          user_id: user?._id,
          step: "brandDeal",
          hasBeenPaidForBrandDeal: value.hasBeenPaidForBrandDeal,
          timestamp: new Date().toISOString(),
        });

        setCurrentStep("funnel");
      } finally {
        setIsSubmitting(false);
      }
    },
  });

  const renderWelcomeStep = () => (
    <div className="mx-auto flex h-full w-full max-w-2xl flex-col items-center justify-center gap-6 px-6">
      <div className="flex flex-col items-center gap-6 text-center">
        <h1 className="text-4xl sm:text-5xl font-bold text-primary">
          Welcome to CreatorGrid!
        </h1>
        <div className="flex flex-col gap-4 text-base sm:text-lg text-primary/80 max-w-xl">
          <p>Our goal is to help you grow and monetize more easily.</p>
          <p>
            As a team of creators ourselves, we know that every journey is
            unique.
          </p>
          <p>We'll ask a few questions to we can tailor CreatorGrid to you.</p>
        </div>
      </div>

      <Button
        onClick={() => setCurrentStep("name")}
        className="w-full max-w-96 mt-4"
        size="lg"
      >
        Get Started
      </Button>
    </div>
  );

  const renderNameStep = () => (
    <div className="mx-auto flex h-full w-full max-w-96 flex-col items-center justify-center gap-6">
      <div className="flex flex-col items-center gap-2">
        <span className="mb-2 select-none text-6xl">👋</span>
        <h3 className="text-center text-2xl font-medium text-primary">
          Welcome!
        </h3>
        <p className="text-center text-base font-normal text-primary/60">
          Let's get started by entering your name.
        </p>
      </div>
      <form
        className="flex w-full flex-col items-start gap-1"
        onSubmit={(e) => {
          e.preventDefault();
          e.stopPropagation();
          nameForm.handleSubmit();
        }}
      >
        <div className="flex w-full flex-col gap-1.5">
          <label htmlFor="name" className="sr-only">
            Name
          </label>
          <nameForm.Field
            name="name"
            validators={{
              onSubmit: validators.name,
            }}
            children={(field) => (
              <Input
                placeholder="Name"
                autoComplete="off"
                required
                value={field.state.value}
                onBlur={field.handleBlur}
                onChange={(e) => field.handleChange(e.target.value)}
                className={`bg-transparent ${
                  field.state.meta?.errors.length > 0 &&
                  "border-destructive focus-visible:ring-destructive"
                }`}
              />
            )}
          />
        </div>

        <div className="flex flex-col">
          {nameForm.state.fieldMeta.name?.errors.length > 0 && (
            <span className="mb-2 text-sm text-destructive dark:text-destructive-foreground">
              {nameForm.state.fieldMeta.name?.errors.join(" ")}
            </span>
          )}
        </div>

        <Button type="submit" size="sm" className="w-full">
          {isSubmitting ? <Loader2 className="animate-spin" /> : "Continue"}
        </Button>
      </form>

      <p className="px-6 text-center text-sm font-normal leading-normal text-primary/60">
        You can update your information at any time from your account settings.
      </p>
    </div>
  );

  const renderCountryStep = () => (
    <div className="mx-auto flex h-full w-full max-w-96 flex-col items-center justify-center gap-6">
      <div className="flex flex-col items-center gap-2">
        <span className="mb-2 select-none text-6xl">🌍</span>
        <h3 className="text-center text-2xl font-medium text-primary">
          Where are you based?
        </h3>
        <p className="text-center text-base font-normal text-primary/60">
          This helps us provide location-specific features and support.
        </p>
      </div>
      <form
        className="flex w-full flex-col items-start gap-1"
        onSubmit={(e) => {
          e.preventDefault();
          e.stopPropagation();
          countryForm.handleSubmit();
        }}
      >
        <div className="flex w-full flex-col gap-1.5">
          <label htmlFor="country" className="sr-only">
            Country
          </label>
          <countryForm.Field
            name="country"
            validators={{
              onSubmit: z.string().min(1, "Please select your country"),
            }}
            children={(field) => (
              <SearchableCountrySelect
                countries={COUNTRIES}
                value={field.state.value}
                onValueChange={(value) => field.handleChange(value)}
                placeholder="Select your country"
                hasError={field.state.meta?.errors.length > 0}
              />
            )}
          />
        </div>

        <div className="flex flex-col">
          {countryForm.state.fieldMeta.country?.errors.length > 0 && (
            <span className="mb-2 text-sm text-destructive dark:text-destructive-foreground">
              {countryForm.state.fieldMeta.country?.errors.join(" ")}
            </span>
          )}
        </div>

        <Button type="submit" size="sm" className="w-full">
          {isSubmitting ? <Loader2 className="animate-spin" /> : "Continue"}
        </Button>
      </form>

      <p className="px-6 text-center text-sm font-normal leading-normal text-primary/60">
        You can update your information at any time from your account settings.
      </p>
    </div>
  );

  const renderFunnelStep = () => {
    const showCustomInput = selectedFunnel === "other";

    return (
      <div className="mx-auto flex h-full w-full max-w-96 flex-col items-center justify-center gap-6">
        <div className="flex flex-col items-center gap-2">
          <span className="mb-2 select-none text-6xl">🤔</span>
          <h3 className="text-center text-2xl font-medium text-primary">
            How did you hear about us?
          </h3>
          <p className="text-center text-base font-normal text-primary/60">
            This helps us understand how people discover our platform.
          </p>
        </div>
        <form
          className="flex w-full flex-col items-start gap-1"
          onSubmit={(e) => {
            e.preventDefault();
            e.stopPropagation();
            funnelForm.handleSubmit();
          }}
        >
          <div className="flex w-full flex-col gap-1.5">
            <label htmlFor="funnel" className="sr-only">
              How did you hear about us
            </label>
            <funnelForm.Field
              name="funnel"
              validators={{
                onSubmit: z.string().min(1, "Please select an option"),
              }}
              children={(field) => (
                <Select
                  value={field.state.value}
                  onValueChange={(value) => {
                    field.handleChange(value);
                    setSelectedFunnel(value);
                  }}
                >
                  <SelectTrigger
                    className={`bg-transparent ${
                      field.state.meta?.errors.length > 0 &&
                      "border-destructive focus-visible:ring-destructive"
                    }`}
                  >
                    <SelectValue placeholder="Select how you heard about us" />
                  </SelectTrigger>
                  <SelectContent>
                    {FUNNEL_OPTIONS.map((option) => (
                      <SelectItem key={option.value} value={option.value}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            />
          </div>

          {showCustomInput && (
            <div className="flex w-full flex-col gap-1.5">
              <label htmlFor="customFunnel" className="sr-only">
                Please specify
              </label>
              <funnelForm.Field
                name="customFunnel"
                validators={{
                  onSubmit: z
                    .string()
                    .refine(
                      (value) =>
                        selectedFunnel !== "other" || (value && value.trim()),
                      "Please specify how you heard about us",
                    ),
                }}
                children={(field) => (
                  <Input
                    placeholder="Please specify..."
                    autoComplete="off"
                    value={field.state.value}
                    onBlur={field.handleBlur}
                    onChange={(e) => field.handleChange(e.target.value)}
                    className={`bg-transparent ${
                      field.state.meta?.errors.length > 0 &&
                      "border-destructive focus-visible:ring-destructive"
                    }`}
                  />
                )}
              />
            </div>
          )}

          <div className="flex flex-col">
            {funnelForm.state.fieldMeta.funnel?.errors.length > 0 && (
              <span className="mb-2 text-sm text-destructive dark:text-destructive-foreground">
                {funnelForm.state.fieldMeta.funnel?.errors.join(" ")}
              </span>
            )}
            {funnelForm.state.fieldMeta.customFunnel?.errors.length > 0 && (
              <span className="mb-2 text-sm text-destructive dark:text-destructive-foreground">
                {funnelForm.state.fieldMeta.customFunnel?.errors.join(" ")}
              </span>
            )}
          </div>

          <Button type="submit" size="sm" className="w-full">
            {isSubmitting ? <Loader2 className="animate-spin" /> : "Continue"}
          </Button>
        </form>

        <p className="px-6 text-center text-sm font-normal leading-normal text-primary/60">
          You can update your information at any time from your account
          settings.
        </p>
      </div>
    );
  };

  const PLATFORM_OPTIONS = [
    { value: "youtube", label: "YouTube" },
    { value: "youtube-shorts", label: "YouTube Shorts" },
    { value: "tiktok", label: "TikTok" },
    { value: "instagram", label: "Instagram" },
  ];

  const renderWeeklyPlatformsStep = () => (
    <div className="mx-auto flex h-full w-full max-w-96 flex-col items-center justify-center gap-6">
      <div className="flex flex-col items-center gap-2">
        <span className="mb-2 select-none text-6xl">📺</span>
        <h3 className="text-center text-2xl font-medium text-primary">
          On which platforms do you publish content weekly?
        </h3>
        <p className="text-center text-base font-normal text-primary/60">
          (Select all that apply)
        </p>
      </div>
      <form
        className="flex w-full flex-col items-start gap-4"
        onSubmit={(e) => {
          e.preventDefault();
          e.stopPropagation();
          weeklyPlatformsForm.handleSubmit();
        }}
      >
        <div className="flex w-full flex-col gap-3">
          {PLATFORM_OPTIONS.map((platform) => (
            <div key={platform.value} className="flex items-center space-x-2">
              <Checkbox
                id={`weekly-${platform.value}`}
                checked={selectedWeeklyPlatforms.includes(platform.value)}
                onCheckedChange={(checked) => {
                  const newPlatforms = checked
                    ? [...selectedWeeklyPlatforms, platform.value]
                    : selectedWeeklyPlatforms.filter(
                        (p) => p !== platform.value,
                      );
                  setSelectedWeeklyPlatforms(newPlatforms);
                  weeklyPlatformsForm.setFieldValue("platforms", newPlatforms);
                }}
              />
              <label
                htmlFor={`weekly-${platform.value}`}
                className="cursor-pointer text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
              >
                {platform.label}
              </label>
            </div>
          ))}
        </div>

        <Button type="submit" size="sm" className="w-full">
          {isSubmitting ? <Loader2 className="animate-spin" /> : "Continue"}
        </Button>

        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="w-full"
          onClick={() => {
            weeklyPlatformsForm.setFieldValue("platforms", []);
            setSelectedWeeklyPlatforms([]);
            weeklyPlatformsForm.handleSubmit();
          }}
        >
          Skip - I don't post weekly
        </Button>
      </form>

      <p className="px-6 text-center text-sm font-normal leading-normal text-primary/60">
        You can update your information at any time from your account settings.
      </p>
    </div>
  );

  const renderChannelLinkStep = () => (
    <div className="mx-auto flex h-full w-full max-w-96 flex-col items-center justify-center gap-6">
      <div className="flex flex-col items-center gap-2">
        <span className="mb-2 select-none text-6xl">🎬</span>
        <h3 className="text-center text-2xl font-medium text-primary">
          Your Main Channel
        </h3>
        <p className="text-center text-base font-normal text-primary/60">
          Share a link to your main video-based channel (if you have one)
        </p>
      </div>
      <form
        className="flex w-full flex-col items-start gap-1"
        onSubmit={(e) => {
          e.preventDefault();
          e.stopPropagation();
          channelLinkForm.handleSubmit();
        }}
      >
        <div className="flex w-full flex-col gap-1.5">
          <label htmlFor="channelLink" className="sr-only">
            Channel Link
          </label>
          <channelLinkForm.Field
            name="channelLink"
            validators={{
              onSubmit: z
                .string()
                .url("Please enter a valid URL")
                .or(z.literal(""))
                .optional(),
            }}
            children={(field) => (
              <Input
                placeholder="https://youtube.com/@yourchannel"
                autoComplete="off"
                value={field.state.value}
                onBlur={field.handleBlur}
                onChange={(e) => field.handleChange(e.target.value)}
                className={`bg-transparent ${
                  field.state.meta?.errors.length > 0 &&
                  "border-destructive focus-visible:ring-destructive"
                }`}
              />
            )}
          />
        </div>

        <div className="flex flex-col">
          {channelLinkForm.state.fieldMeta.channelLink?.errors.length > 0 && (
            <span className="mb-2 text-sm text-destructive dark:text-destructive-foreground">
              {channelLinkForm.state.fieldMeta.channelLink?.errors.join(" ")}
            </span>
          )}
        </div>

        <Button type="submit" size="sm" className="w-full">
          {isSubmitting ? <Loader2 className="animate-spin" /> : "Continue"}
        </Button>

        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="w-full"
          onClick={() => {
            channelLinkForm.setFieldValue("channelLink", "");
            channelLinkForm.handleSubmit();
          }}
        >
          Skip - I don't have a channel
        </Button>
      </form>

      <p className="px-6 text-center text-sm font-normal leading-normal text-primary/60">
        You can update your information at any time from your account settings.
      </p>
    </div>
  );

  const renderBrandDealStep = () => (
    <div className="mx-auto flex h-full w-full max-w-96 flex-col items-center justify-center gap-6">
      <div className="flex flex-col items-center gap-2">
        <span className="mb-2 select-none text-6xl">💼</span>
        <h3 className="text-center text-2xl font-medium text-primary">
          Have you ever been paid for a brand deal?
        </h3>
        <p className="text-center text-base font-normal text-primary/60">
          This helps us understand your experience level.
        </p>
      </div>
      <div className="flex w-full flex-col gap-3">
        <Button
          type="button"
          variant="outline"
          size="lg"
          className={`w-full ${
            onboardingData.hasBeenPaidForBrandDeal === true
              ? "border-primary bg-primary/10"
              : ""
          }`}
          onClick={() => {
            setOnboardingData((prev) => ({
              ...prev,
              hasBeenPaidForBrandDeal: true,
            }));
            brandDealForm.setFieldValue("hasBeenPaidForBrandDeal", true);
            brandDealForm.handleSubmit();
          }}
          disabled={isSubmitting}
        >
          {isSubmitting && onboardingData.hasBeenPaidForBrandDeal === true ? (
            <Loader2 className="animate-spin" />
          ) : (
            "Yes"
          )}
        </Button>
        <Button
          type="button"
          variant="outline"
          size="lg"
          className={`w-full ${
            onboardingData.hasBeenPaidForBrandDeal === false
              ? "border-primary bg-primary/10"
              : ""
          }`}
          onClick={() => {
            setOnboardingData((prev) => ({
              ...prev,
              hasBeenPaidForBrandDeal: false,
            }));
            brandDealForm.setFieldValue("hasBeenPaidForBrandDeal", false);
            brandDealForm.handleSubmit();
          }}
          disabled={isSubmitting}
        >
          {isSubmitting && onboardingData.hasBeenPaidForBrandDeal === false ? (
            <Loader2 className="animate-spin" />
          ) : (
            "No"
          )}
        </Button>
      </div>

      <p className="px-6 text-center text-sm font-normal leading-normal text-primary/60">
        Don't worry, there's no wrong answer! This just helps us personalize
        your experience.
      </p>
    </div>
  );

  if (currentStep === "welcome") {
    return renderWelcomeStep();
  } else if (currentStep === "name") {
    return renderNameStep();
  } else if (currentStep === "country") {
    return renderCountryStep();
  } else if (currentStep === "funnel") {
    return renderFunnelStep();
  } else if (currentStep === "weeklyPlatforms") {
    return renderWeeklyPlatformsStep();
  } else if (currentStep === "channelLink") {
    return renderChannelLinkStep();
  } else if (currentStep === "brandDeal") {
    return renderBrandDealStep();
  }

  return null;
}
